import type { FieldErrors, Resolver } from 'react-hook-form';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { IBAN_MAX_LENGTH } from '~/constants/common.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import type { TCurrency } from '~/queries/types.ts';
import {
  GUARANTEES_TENDER_ID_PATTERN,
  normalizeGuaranteesTenderId,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  GuaranteesApplicationStatus,
  GuaranteeOperationType,
  GuaranteeSecurityType,
  type TGuaranteesAccount,
  type TGuaranteesApplication,
  type TGuaranteesApplicationSelectionPatchResult,
  type TGuaranteesMainDeal,
  type TGuaranteesSecuritySectionDto,
  type TRootApplicationSectionDto,
} from '~/store/guarantees/types.ts';
import {
  DEFAULT_GUARANTEE_CURRENCY,
  GUARANTEE_CURRENCY_OPTIONS,
  GUARANTEE_OPERATION_TYPE_OPTIONS,
  GUARANTEE_SECURITY_TYPE_OPTIONS,
  PRINCIPAL_CONSTITUENT_DOCUMENTS_CHANGES_OPTIONS,
} from './constants.ts';
import {
  GuaranteesAccountTargetField,
  GuaranteesPrincipalConstituentDocumentsChangesOption,
  type TGuaranteesAccountSecurityType,
  type TGuaranteesAccountOption,
  type TGuaranteesContractOption,
  type TGuaranteesRootSectionFormErrors,
  type TGuaranteesRootSectionFormValues,
} from './types.ts';
import {
  getGuaranteesAccountFieldBySecurityType,
  getGuaranteesCounterpartyAccountOptions,
  getGuaranteesPrincipalDebitAccountOptions,
  isGuaranteesPrincipalDebitAccountIban,
  isGuaranteesOurBankIban,
  normalizeGuaranteesIban,
} from './accountUtils.ts';

const SELECTED_ACCOUNT_STORAGE_PREFIX = 'guarantees:selected-account';
const SELECTED_CONTRACT_STORAGE_PREFIX = 'guarantees:selected-contract';

export type TGuaranteesStoredAccountSelection = {
  iban: string;
  targetField: GuaranteesAccountTargetField;
};

export type TGuaranteesStoredContractSelection = {
  mainDealId: string;
  mainDealNumber: string;
  mainDealDate: string;
  mainDealDateFrom: string;
  mainDealDateTo: string;
  mainDealCurrency: string;
  mainDealAmount: string;
  mainDealLimitBalance: string;
};

const isGuaranteeOperationType = (value: unknown): value is GuaranteeOperationType =>
  Object.values(GuaranteeOperationType).includes(value as GuaranteeOperationType);

export const isGuaranteeOperationTypeProzorro = (
  operationType: GuaranteeOperationType | null | undefined
): boolean =>
  operationType === GuaranteeOperationType.TENDER_PROZORRO ||
  operationType === GuaranteeOperationType.PERFORMANCE_PROZORRO;

export const isGuaranteesApplicationSelectionPatchSuccessful = (
  result: TGuaranteesApplicationSelectionPatchResult | null | undefined
): boolean => result?.status === 200 || result?.status === 204;

export const areGuaranteesRequiredSelectionsComplete = ({
  hasMainDealSelection,
  hasTenderSelection,
  isProzorroTender,
}: {
  hasMainDealSelection: boolean;
  hasTenderSelection: boolean;
  isProzorroTender: boolean;
}): boolean => hasMainDealSelection && (!isProzorroTender || hasTenderSelection);

const isSelectableGuaranteeSecurityType = (
  value: unknown
): value is GuaranteeSecurityType =>
  value === GuaranteeSecurityType.NO_SECURITY ||
  value === GuaranteeSecurityType.COVERAGE_ACCOUNT ||
  value === GuaranteeSecurityType.DEPOSIT_RIGHTS;

const isAccountGuaranteeSecurityType = (
  value: unknown
): value is TGuaranteesAccountSecurityType =>
  value === GuaranteeSecurityType.COVERAGE_ACCOUNT || value === GuaranteeSecurityType.DEPOSIT_RIGHTS;

const getRootValue = (root: TGuaranteesApplication['root'], keys: string[]): unknown => {
  if (!root) {
    return undefined;
  }

  return keys.map((key) => root[key]).find((value) => value !== undefined && value !== null);
};

const getRootStringValue = (root: TGuaranteesApplication['root'], keys: string[]): string =>
  String(getRootValue(root, keys) ?? '').trim();

const getSectionStringValue = (section: Record<string, unknown> | undefined, key: string): string =>
  String(section?.[key] ?? '').trim();

const getSectionBooleanValue = (
  section: Record<string, unknown> | undefined,
  key: string,
  fallback = false
): boolean => {
  const value = section?.[key];

  return typeof value === 'boolean' ? value : fallback;
};

const findPrincipalConstituentDocumentsChangesOption = (
  value: GuaranteesPrincipalConstituentDocumentsChangesOption
) =>
  PRINCIPAL_CONSTITUENT_DOCUMENTS_CHANGES_OPTIONS.find((option) => option.value === value) ??
  null;

const toDateInputValue = (value: string): string => {
  const normalizedValue = value.trim();
  const isoDateMatch = normalizedValue.match(/^\d{4}-\d{2}-\d{2}/);

  return isoDateMatch?.[0] ?? normalizedValue;
};

const toInstantPayloadValue = (value: string): string => {
  const normalizedValue = value.trim();

  const isoDateMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const displayDateMatch = normalizedValue.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);

  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;

    return buildUtcStartOfDayInstant(Number(year), Number(month), Number(day)) ?? normalizedValue;
  }

  if (displayDateMatch) {
    const [, day, month, year] = displayDateMatch;

    return buildUtcStartOfDayInstant(Number(year), Number(month), Number(day)) ?? normalizedValue;
  }

  if (/\d{4}-\d{2}-\d{2}T/.test(normalizedValue) && !Number.isNaN(Date.parse(normalizedValue))) {
    return new Date(normalizedValue).toISOString();
  }

  return normalizedValue;
};

const buildUtcStartOfDayInstant = (
  year: number,
  month: number,
  day: number
): string | null => {
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date.toISOString();
};

const getRootBooleanValue = (
  root: TGuaranteesApplication['root'],
  keys: string[],
  fallback = false
): boolean => {
  const value = getRootValue(root, keys);

  return typeof value === 'boolean' ? value : fallback;
};

const getSelectedAccountStorageKey = (applicationId: string): string =>
  `${SELECTED_ACCOUNT_STORAGE_PREFIX}:${applicationId}`;

const getSelectedContractStorageKey = (applicationId: string): string =>
  `${SELECTED_CONTRACT_STORAGE_PREFIX}:${applicationId}`;

export const getGuaranteesStoredAccountSelection = (
  applicationId: string
): TGuaranteesStoredAccountSelection | null => {
  if (!applicationId || typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(getSelectedAccountStorageKey(applicationId));

    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue) as Partial<TGuaranteesStoredAccountSelection>;
    const iban = String(parsedValue.iban ?? '').trim();
    const targetField = parsedValue.targetField;

    if (
      !iban ||
      (targetField !== GuaranteesAccountTargetField.CoverageAccountIban &&
        targetField !== GuaranteesAccountTargetField.DepositAccountIban)
    ) {
      return null;
    }

    return { iban, targetField };
  } catch {
    return null;
  }
};

export const saveGuaranteesStoredAccountSelection = (
  applicationId: string,
  account: TGuaranteesAccountOption
) => {
  if (!applicationId || typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    getSelectedAccountStorageKey(applicationId),
    JSON.stringify({
      iban: account.iban,
      targetField: account.targetField,
    } satisfies TGuaranteesStoredAccountSelection)
  );
};
export const getGuaranteesStoredContractSelection = (
  applicationId: string
): TGuaranteesStoredContractSelection | null => {
  if (!applicationId || typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(getSelectedContractStorageKey(applicationId));

    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue) as Partial<TGuaranteesStoredContractSelection>;
    const mainDealId = String(parsedValue.mainDealId ?? '').trim();
    const mainDealNumber = String(parsedValue.mainDealNumber ?? '').trim();

    if (!mainDealId && !mainDealNumber) {
      return null;
    }

    return {
      mainDealId,
      mainDealNumber,
      mainDealDate: toDateInputValue(String(parsedValue.mainDealDate ?? '').trim()),
      mainDealDateFrom: toDateInputValue(String(parsedValue.mainDealDateFrom ?? '').trim()),
      mainDealDateTo: toDateInputValue(String(parsedValue.mainDealDateTo ?? '').trim()),
      mainDealCurrency: String(parsedValue.mainDealCurrency ?? '').trim(),
      mainDealAmount: String(parsedValue.mainDealAmount ?? '').trim(),
      mainDealLimitBalance: String(parsedValue.mainDealLimitBalance ?? '').trim(),
    };
  } catch {
    return null;
  }
};

export const saveGuaranteesStoredContractSelection = (
  applicationId: string,
  contract: TGuaranteesContractOption
) => {
  if (!applicationId || typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    getSelectedContractStorageKey(applicationId),
    JSON.stringify({
      mainDealId: contract.mainDealId,
      mainDealNumber: contract.mainDealNumber,
      mainDealDate: contract.mainDealDate,
      mainDealDateFrom: contract.mainDealDateFrom,
      mainDealDateTo: contract.mainDealDateTo,
      mainDealCurrency: contract.mainDealCurrency,
      mainDealAmount: contract.mainDealAmount,
      mainDealLimitBalance: contract.mainDealLimitBalance,
    } satisfies TGuaranteesStoredContractSelection)
  );
};

export const findGuaranteesRootSectionOperationOption = (
  value?: GuaranteeOperationType | string | null
): TAutocompleteOption | null => {
  if (!isGuaranteeOperationType(value)) {
    return null;
  }

  return GUARANTEE_OPERATION_TYPE_OPTIONS.find((option) => option.value === value) ?? null;
};

export const findGuaranteesRootSectionCurrencyOption = (
  value?: string | null,
  currencyOptions: TAutocompleteOption[] = GUARANTEE_CURRENCY_OPTIONS
): TAutocompleteOption | null => {
  const normalizedValue = String(value ?? '').trim();

  if (!normalizedValue) {
    return null;
  }

  return (
    currencyOptions.find((option) => option.value === normalizedValue) ?? {
      value: normalizedValue,
      label: normalizedValue,
    }
  );
};

const findGuaranteesRootSectionCurrencyOptions = (
  values: unknown,
  currencyOptions: TAutocompleteOption[] = GUARANTEE_CURRENCY_OPTIONS
): TAutocompleteOption[] => {
  const seenCurrencies = new Set<string>();
  const currencies = Array.isArray(values) ? values : [];

  return currencies
    .map((value) => String(value ?? '').trim().toUpperCase())
    .filter((value) => {
      if (!value || seenCurrencies.has(value)) {
        return false;
      }

      seenCurrencies.add(value);
      return true;
    })
    .map((value) => findGuaranteesRootSectionCurrencyOption(value, currencyOptions))
    .filter((option): option is TAutocompleteOption => !!option);
};

export const buildGuaranteesCurrencyOptions = (
  currencies: TCurrency[] | undefined
): TAutocompleteOption[] => {
  const currencyOptionsByCode = new Map<string, TAutocompleteOption>();

  (currencies ?? []).forEach((currency) => {
      const symbolCode = String(currency.symbolCode ?? '').trim();

      if (!symbolCode) {
      return;
      }

      const name = String(currency.name ?? '').trim();

    if (!currencyOptionsByCode.has(symbolCode)) {
      currencyOptionsByCode.set(symbolCode, {
        value: symbolCode,
        label: name ? `${symbolCode} - ${name}` : symbolCode,
      });
    }
  });

  return [...currencyOptionsByCode.values()]
    .sort((firstOption, secondOption) => firstOption.value.localeCompare(secondOption.value));
};

export const findGuaranteesRootSectionSecurityTypeOption = (
  value?: GuaranteeSecurityType | string | null
): TAutocompleteOption | null => {
  if (!isSelectableGuaranteeSecurityType(value)) {
    return null;
  }

  return GUARANTEE_SECURITY_TYPE_OPTIONS.find((option) => option.value === value) ?? null;
};

export const getSelectedGuaranteeOperationType = (
  option?: TAutocompleteOption | null
): GuaranteeOperationType | null => (isGuaranteeOperationType(option?.value) ? option.value : null);

export const isGuaranteesOperationTypeChanged = ({
  selectedOperationType,
  savedOperationType,
}: {
  selectedOperationType: GuaranteeOperationType | null;
  savedOperationType: GuaranteeOperationType | null;
}): boolean => selectedOperationType !== savedOperationType;

export const getSelectedGuaranteeSecurityType = (
  option?: TAutocompleteOption | null
): GuaranteeSecurityType | null => {
  const value = option?.value;

  return isSelectableGuaranteeSecurityType(value) ? value : null;
};

export const getSelectedGuaranteeAccountSecurityType = (
  option?: TAutocompleteOption | null
): TGuaranteesAccountSecurityType | null => {
  const value = option?.value;

  return isAccountGuaranteeSecurityType(value) ? value : null;
};

export const getGuaranteesRootSavedOperationType = (
  application?: TGuaranteesApplication | null
): GuaranteeOperationType | null => {
  const operationType = getRootValue(application?.root, [
    'guaranteeOperationType',
    'guarantee_operation_type',
  ]);

  return isGuaranteeOperationType(operationType) ? operationType : null;
};

export const getGuaranteesRootTenderPublicId = (
  application?: TGuaranteesApplication | null
): string =>
  getRootStringValue(application?.root, ['tenderPublicId', 'prozorroTenderId']);

export const getGuaranteesApplicationMainDealId = (
  application?: TGuaranteesApplication | null,
  storedContractSelection?: TGuaranteesStoredContractSelection | null
): string => {
  const guaranteeTerms = application?.guaranteeTerms as Record<string, unknown> | undefined;

  return (
    getRootStringValue(application?.root, ['mainDealId']) ||
    getSectionStringValue(guaranteeTerms, 'mainDealId') ||
    String(storedContractSelection?.mainDealId ?? '').trim()
  );
};

const buildContractOption = ({
  mainDealId = '',
  mainDealNumber = '',
  mainDealDate = '',
  mainDealDateFrom = '',
  mainDealDateTo = '',
  mainDealCurrency = '',
  mainDealAmount = '',
  mainDealLimitBalance = '',
}: Partial<Omit<TGuaranteesContractOption, 'value' | 'label'>>): TGuaranteesContractOption | null => {
  const optionParts = [mainDealNumber, mainDealDate, mainDealDateFrom, mainDealAmount].filter(
    Boolean
  );

  if (!optionParts.length) {
    return null;
  }

  const value = [
    mainDealId,
    mainDealNumber,
    mainDealDate,
    mainDealDateFrom,
    mainDealDateTo,
    mainDealCurrency,
    mainDealAmount,
    mainDealLimitBalance,
  ]
    .filter(Boolean)
    .join('|');

  return {
    value,
    label: optionParts.join(' / '),
    mainDealId,
    mainDealNumber,
    mainDealDate,
    mainDealDateFrom,
    mainDealDateTo,
    mainDealCurrency,
    mainDealAmount,
    mainDealLimitBalance,
  };
};

export const getGuaranteesContractOptions = (
  mainDeals: TGuaranteesMainDeal[] = []
): TGuaranteesContractOption[] =>
  mainDeals
    .map((deal) =>
      buildContractOption({
        mainDealId: String(deal.mainDealId ?? deal.id ?? deal.dealId ?? '').trim(),
        mainDealNumber: String(deal.dealNumber ?? '').trim(),
        mainDealDate: toDateInputValue(String(deal.mainDealDate ?? '').trim()),
        mainDealDateFrom: toDateInputValue(String(deal.mainDealDateFrom ?? '').trim()),
        mainDealDateTo: toDateInputValue(String(deal.mainDealDateTo ?? '').trim()),
        mainDealCurrency: String(deal.mainDealCurrency ?? '').trim(),
        mainDealAmount: String(deal.mainDealAmount ?? '').trim(),
        mainDealLimitBalance: String(deal.mainDealLimitBalance ?? '').trim(),
      })
    )
    .filter((option): option is TGuaranteesContractOption => !!option);

export const getGuaranteesRootSectionInitialValues = (
  application?: TGuaranteesApplication | null,
  accounts: TGuaranteesAccount[] = [],
  principalDebitAccounts: TGuaranteesAccount[] = [],
  storedAccountSelection?: TGuaranteesStoredAccountSelection | null,
  storedContractSelection?: TGuaranteesStoredContractSelection | null,
  guaranteeCurrencyOptions: TAutocompleteOption[] = GUARANTEE_CURRENCY_OPTIONS,
  securityCurrencies: string[] = []
): TGuaranteesRootSectionFormValues => {
  const root = application?.root;
  const operationType = getGuaranteesRootSavedOperationType(application);
  const amount =
    getRootValue(root, ['guaranteeAmount', 'guarantee_amount']) ??
    application?.guaranteeTerms?.guaranteeAmount;
  const currency =
    getRootValue(root, ['guaranteeCurrency', 'guarantee_currency']) ??
    application?.guaranteeTerms?.guaranteeCurrency;
  const guaranteeExpiryDate =
    getRootValue(root, ['guaranteeExpiryDate', 'guarantee_expiry_date']) ??
    application?.guaranteeTerms?.guaranteeExpiryDate ??
    (application?.managerTerms as Record<string, unknown> | undefined)?.guaranteeExpiryDate;
  const securityAmount =
    getSectionStringValue(application?.security, 'amount') ||
    getSectionStringValue(application?.security, 'securityAmount') ||
    getSectionStringValue(application?.security, 'coverageAmount') ||
    getRootValue(root, ['securityAmount', 'security_amount']);
  const securityType =
    getSectionStringValue(application?.security, 'type') ||
    getSectionStringValue(application?.security, 'securityType') ||
    getRootValue(root, ['securityType', 'security_type']);
  const securityTypeOption = findGuaranteesRootSectionSecurityTypeOption(
    String(securityType ?? '')
  );
  getSelectedGuaranteeSecurityType(securityTypeOption);
  const selectedAccountSecurityType = getSelectedGuaranteeAccountSecurityType(securityTypeOption);
  const storedAccountMatchesSecurityType =
    (selectedAccountSecurityType === GuaranteeSecurityType.DEPOSIT_RIGHTS &&
      storedAccountSelection?.targetField === GuaranteesAccountTargetField.DepositAccountIban) ||
    (selectedAccountSecurityType === GuaranteeSecurityType.COVERAGE_ACCOUNT &&
      storedAccountSelection?.targetField === GuaranteesAccountTargetField.CoverageAccountIban);
  const storedDepositAccountIban =
    storedAccountMatchesSecurityType &&
    storedAccountSelection?.targetField === GuaranteesAccountTargetField.DepositAccountIban
      ? storedAccountSelection.iban
      : '';
  const storedCoverageAccountIban =
    storedAccountMatchesSecurityType &&
    storedAccountSelection?.targetField === GuaranteesAccountTargetField.CoverageAccountIban
      ? storedAccountSelection.iban
      : '';
  const depositAccountIban =
    getRootStringValue(root, ['depositAccountIban', 'deposit_account_iban']) ||
    getSectionStringValue(application?.security, 'depositAccountIban') ||
    storedDepositAccountIban;
  const coverageAccountIban =
    getRootStringValue(root, ['coverageAccountIban', 'coverage_account_iban']) ||
    getSectionStringValue(application?.security, 'coverageAccountIban') ||
    storedCoverageAccountIban;
  const accountOptions = getGuaranteesCounterpartyAccountOptions(accounts);
  const accountTargetField = getGuaranteesAccountFieldBySecurityType(selectedAccountSecurityType);
  const accountIban =
    selectedAccountSecurityType === GuaranteeSecurityType.DEPOSIT_RIGHTS
      ? depositAccountIban
      : coverageAccountIban;
  const selectedAccount =
    selectedAccountSecurityType && accountIban
      ? (accountOptions.find(
          (option) =>
            option.targetField === accountTargetField &&
            option.iban.toUpperCase() === normalizeGuaranteesIban(accountIban)
        ) ?? null)
      : null;
  const isCounterpartyAccount = accountIban ? !!selectedAccount : true;
  const principalDebitAccountIban = getSectionStringValue(
    application?.security,
    'principalDebitAccountIban'
  );
  const principalDebitAccountOptions =
    getGuaranteesPrincipalDebitAccountOptions(principalDebitAccounts);
  const selectedPrincipalDebitAccount =
    principalDebitAccountIban
      ? (principalDebitAccountOptions.find(
          (option) =>
            option.iban.toUpperCase() === normalizeGuaranteesIban(principalDebitAccountIban)
        ) ?? null)
      : null;
  const savedSecurityCurrencies = securityCurrencies.length
    ? securityCurrencies
    : application?.security?.currencies;
  const guaranteeTerms = application?.guaranteeTerms as Record<string, unknown> | undefined;
  const mainDealId =
    getRootStringValue(root, ['mainDealId']) ||
    getSectionStringValue(guaranteeTerms, 'mainDealId') ||
    storedContractSelection?.mainDealId ||
    '';
  const mainDealNumber =
    getRootStringValue(root, ['mainDealNumber']) ||
    getSectionStringValue(guaranteeTerms, 'mainDealNumber') ||
    storedContractSelection?.mainDealNumber ||
    '';
  const mainDealDate =
    toDateInputValue(
      getRootStringValue(root, ['mainDealDate']) ||
        getSectionStringValue(guaranteeTerms, 'mainDealDate')
    ) ||
    storedContractSelection?.mainDealDate ||
    '';
  const mainDealDateFrom =
    toDateInputValue(
      getRootStringValue(root, ['mainDealDateFrom']) ||
        getSectionStringValue(guaranteeTerms, 'mainDealDateFrom')
    ) ||
    storedContractSelection?.mainDealDateFrom ||
    '';
  const mainDealDateTo =
    toDateInputValue(
      getRootStringValue(root, ['mainDealDateTo']) ||
        getSectionStringValue(guaranteeTerms, 'mainDealDateTo')
    ) ||
    storedContractSelection?.mainDealDateTo ||
    '';
  const mainDealCurrency =
    getRootStringValue(root, ['mainDealCurrency']) ||
    getSectionStringValue(guaranteeTerms, 'mainDealCurrency') ||
    storedContractSelection?.mainDealCurrency ||
    '';
  const mainDealAmount =
    getRootStringValue(root, ['mainDealAmount']) ||
    getSectionStringValue(guaranteeTerms, 'mainDealAmount') ||
    storedContractSelection?.mainDealAmount ||
    '';
  const mainDealLimitBalance =
    getRootStringValue(root, ['mainDealLimitBalance']) ||
    getSectionStringValue(guaranteeTerms, 'mainDealLimitBalance') ||
    storedContractSelection?.mainDealLimitBalance ||
    '';
  const selectedContract = buildContractOption({
    mainDealId,
    mainDealNumber,
    mainDealDate,
    mainDealDateFrom,
    mainDealDateTo,
    mainDealCurrency,
    mainDealAmount,
    mainDealLimitBalance,
  });
  const operationTypeOther = getRootValue(root, [
    'guaranteeOperationTypeOther',
    'guarantee_operation_type_other',
  ]);

  return {
    guaranteeOperationType: findGuaranteesRootSectionOperationOption(operationType),
    guaranteeOperationTypeOther: String(operationTypeOther ?? ''),
    guaranteeAmount: amount === undefined || amount === null ? '' : String(amount),
    guaranteeCurrency: findGuaranteesRootSectionCurrencyOption(
      String(currency ?? DEFAULT_GUARANTEE_CURRENCY),
      guaranteeCurrencyOptions
    ),
    guaranteeExpiryDate: toDateInputValue(String(guaranteeExpiryDate ?? '')),
    securityAmount:
      securityAmount === undefined || securityAmount === null ? '' : String(securityAmount),
    securityCurrencies: findGuaranteesRootSectionCurrencyOptions(
      savedSecurityCurrencies,
      guaranteeCurrencyOptions
    ),
    requiredSecurityAmount: getSectionStringValue(application?.security, 'requiredSecurityAmount'),
    pledgorName: getSectionStringValue(application?.security, 'pledgorName'),
    pledgorHasChangesInConstituentDocuments: getSectionBooleanValue(
      application?.security,
      'pledgorHasChangesInConstituentDocuments'
    )
      ? findPrincipalConstituentDocumentsChangesOption(
          GuaranteesPrincipalConstituentDocumentsChangesOption.YES
        )
      : findPrincipalConstituentDocumentsChangesOption(
          GuaranteesPrincipalConstituentDocumentsChangesOption.NO
        ),
    pledgorIdentifyCode: getSectionStringValue(application?.security, 'pledgorIdentifyCode'),
    isCounterpartyPrincipalDebitAccount: principalDebitAccountIban
      ? !!selectedPrincipalDebitAccount
      : true,
    principalDebitAccount: selectedPrincipalDebitAccount,
    principalDebitAccountIban,
    tenderSearch: getGuaranteesRootTenderPublicId(application),
    mainDealId,
    mainDealNumber,
    mainDealDate,
    mainDealDateFrom,
    mainDealDateTo,
    mainDealCurrency: findGuaranteesRootSectionCurrencyOption(mainDealCurrency),
    mainDealAmount,
    mainDealLimitBalance,
    checkAccountsRestrictions: getRootBooleanValue(root, ['checkAccountsRestrictions']),
    checkAccountRestriction: getRootBooleanValue(root, ['checkAccountsRestrictions']),
    isUrgent: getRootBooleanValue(root, ['isUrgent', 'is_urgent', 'urgent']),
    securityType: securityTypeOption,
    isCounterpartyAccount,
    coverageAmount: getSectionStringValue(application?.security, 'coverageAmount'),
    depositAccountIban,
    coverageAccountIban,
    contract: selectedContract,
    account: selectedAccount,
  };
};

export const validateGuaranteesTenderPublicId = (value: string): string => {
  const normalizedTenderId = normalizeGuaranteesTenderId(value);

  if (!normalizedTenderId) {
    return errorMessages.FIELD_REQUIRED;
  }

  if (!GUARANTEES_TENDER_ID_PATTERN.test(normalizedTenderId)) {
    return errorMessages.INVALID_INPUT_DATA;
  }

  return '';
};

export const validateGuaranteesRootSectionForm = (
  values: TGuaranteesRootSectionFormValues
): TGuaranteesRootSectionFormErrors => {
  const errors: TGuaranteesRootSectionFormErrors = {};
  const operationType = getSelectedGuaranteeOperationType(values.guaranteeOperationType);

  if (!operationType) {
    errors.guaranteeOperationType = errorMessages.OPTION_REQUIRED;

    return errors;
  }

  if (
    operationType === GuaranteeOperationType.OTHER &&
    !values.guaranteeOperationTypeOther.trim()
  ) {
    errors.guaranteeOperationTypeOther = errorMessages.FIELD_REQUIRED;
  }

  if (!isGuaranteeOperationTypeProzorro(operationType)) {
    const normalizedAmount = values.guaranteeAmount.trim();

    if (!normalizedAmount) {
      errors.guaranteeAmount = errorMessages.FIELD_REQUIRED;
    } else if (!Number.isFinite(Number(normalizedAmount))) {
      errors.guaranteeAmount = errorMessages.MUST_BE_NUMBER;
    } else if (Number(normalizedAmount) <= 0) {
      errors.guaranteeAmount = errorMessages.MUST_BE_GREATER_THAN_ZERO;
    }

    if (!values.guaranteeCurrency?.value.trim()) {
      errors.guaranteeCurrency = errorMessages.FIELD_REQUIRED;
    }

    if (!values.guaranteeExpiryDate.trim()) {
      errors.guaranteeExpiryDate = errorMessages.FIELD_REQUIRED;
    }
  }

  if (isGuaranteeOperationTypeProzorro(operationType)) {
    return errors;
  }

  return errors;
};

const validateGuaranteesIban = (
  value: string,
  fieldName: GuaranteesAccountTargetField,
  errors: TGuaranteesRootSectionFormErrors
) => {
  const normalizedIban = normalizeGuaranteesIban(value);

  if (!normalizedIban) {
    errors[fieldName] = errorMessages.FIELD_REQUIRED;

    return;
  }

  if (normalizedIban.length !== IBAN_MAX_LENGTH) {
    errors[fieldName] = errorMessages.INCOMPLETE_IBAN;

    return;
  }

  if (!isGuaranteesOurBankIban(normalizedIban)) {
    errors[fieldName] = errorMessages.INVALID_IBAN;
  }
};

const getSecurityCurrencyValues = (values: TAutocompleteOption[] = []): string[] => {
  const seenCurrencies = new Set<string>();

  return values
    .map((option) => String(option?.value ?? '').trim().toUpperCase())
    .filter((value) => {
      if (!value || seenCurrencies.has(value)) {
        return false;
      }

      seenCurrencies.add(value);
      return true;
    });
};

const validatePrincipalDebitAccountIban = (
  value: string,
  errors: TGuaranteesRootSectionFormErrors,
  fieldName: 'principalDebitAccount' | 'principalDebitAccountIban',
  shouldValidateAccountPrefix: boolean
) => {
  const normalizedIban = normalizeGuaranteesIban(value);

  if (!normalizedIban) {
    errors[fieldName] = errorMessages.FIELD_REQUIRED;

    return;
  }

  if (normalizedIban.length !== IBAN_MAX_LENGTH) {
    errors[fieldName] = errorMessages.INCOMPLETE_IBAN;

    return;
  }

  if (!isGuaranteesOurBankIban(normalizedIban)) {
    errors[fieldName] = errorMessages.INVALID_IBAN;

    return;
  }

  if (
    shouldValidateAccountPrefix &&
    !isGuaranteesPrincipalDebitAccountIban(normalizedIban)
  ) {
    errors[fieldName] = errorMessages.INVALID_IBAN;
  }
};

export const validateGuaranteesSecretSectionForm = (
  values: TGuaranteesRootSectionFormValues
): TGuaranteesRootSectionFormErrors => {
  const errors: TGuaranteesRootSectionFormErrors = {};
  const normalizedSecurityAmount = values.securityAmount.trim();
  const securityAmount = normalizedSecurityAmount ? Number(normalizedSecurityAmount) : null;
  const securityType = getSelectedGuaranteeSecurityType(values.securityType);

  if (!securityType) {
    errors.securityType = errorMessages.OPTION_REQUIRED;
  }

  if (values.securityType?.value && !securityType) {
    errors.securityType = errorMessages.INVALID_INPUT_DATA;
  }

  if (!normalizedSecurityAmount) {
    errors.securityAmount = errorMessages.FIELD_REQUIRED;
  } else if (!Number.isFinite(securityAmount) || (securityAmount ?? 0) < 0) {
    errors.securityAmount = errorMessages.MUST_BE_NUMBER;
  }

  if (
    securityType !== GuaranteeSecurityType.NO_SECURITY &&
    getSecurityCurrencyValues(values.securityCurrencies).length === 0
  ) {
    errors.securityCurrencies = errorMessages.OPTION_REQUIRED;
  }

  if (
    securityType !== GuaranteeSecurityType.NO_SECURITY &&
    getSecurityCurrencyValues(values.securityCurrencies).some((currency) => currency.length !== 3)
  ) {
    errors.securityCurrencies = errorMessages.INVALID_INPUT_DATA;
  }

  if (!values.requiredSecurityAmount.trim()) {
    errors.requiredSecurityAmount = errorMessages.FIELD_REQUIRED;
  }

  if (!values.pledgorName.trim()) {
    errors.pledgorName = errorMessages.FIELD_REQUIRED;
  }

  if (
    values.pledgorHasChangesInConstituentDocuments?.value ===
      GuaranteesPrincipalConstituentDocumentsChangesOption.YES &&
    !values.pledgorIdentifyCode.trim()
  ) {
    errors.pledgorIdentifyCode = errorMessages.FIELD_REQUIRED;
  }

  if (values.isCounterpartyPrincipalDebitAccount && !values.principalDebitAccount?.value) {
    errors.principalDebitAccount = errorMessages.OPTION_REQUIRED;
  }

  validatePrincipalDebitAccountIban(
    values.principalDebitAccountIban,
    errors,
    values.isCounterpartyPrincipalDebitAccount
      ? 'principalDebitAccount'
      : 'principalDebitAccountIban',
    !values.isCounterpartyPrincipalDebitAccount
  );

  if (
    securityType !== GuaranteeSecurityType.NO_SECURITY &&
    values.isCounterpartyAccount &&
    !values.account?.value
  ) {
    errors.account = errorMessages.OPTION_REQUIRED;
  }

  if (securityType === GuaranteeSecurityType.DEPOSIT_RIGHTS) {
    validateGuaranteesIban(
      values.depositAccountIban,
      GuaranteesAccountTargetField.DepositAccountIban,
      errors
    );

    if (values.coverageAccountIban.trim()) {
      errors.coverageAccountIban = errorMessages.INVALID_INPUT_DATA;
    }
  }

  if (securityType === GuaranteeSecurityType.COVERAGE_ACCOUNT) {
    validateGuaranteesIban(
      values.coverageAccountIban,
      GuaranteesAccountTargetField.CoverageAccountIban,
      errors
    );

    if (values.depositAccountIban.trim()) {
      errors.depositAccountIban = errorMessages.INVALID_INPUT_DATA;
    }
  }

  return errors;
};

export const areRequiredValuesFilled = <TValues extends Record<string, unknown>>(
  values: TValues,
  fieldNames: readonly (keyof TValues)[]
): boolean => fieldNames.every((fieldName) => String(values[fieldName] ?? '').trim());

const toResolverErrors = (
  errors: TGuaranteesRootSectionFormErrors
): FieldErrors<TGuaranteesRootSectionFormValues> =>
  Object.entries(errors).reduce<FieldErrors<TGuaranteesRootSectionFormValues>>(
    (accumulator, [name, message]) => {
      if (!message) {
        return accumulator;
      }

      return {
        ...accumulator,
        [name]: {
          type: 'manual',
          message,
        },
      };
    },
    {}
  );

export const guaranteesRootSectionResolver: Resolver<TGuaranteesRootSectionFormValues> = async (
  values
) => {
  const errors = validateGuaranteesRootSectionForm(values);

  return {
    values: Object.keys(errors).length ? {} : values,
    errors: toResolverErrors(errors),
  };
};

export const buildGuaranteesRootSectionPayload = (
  values: TGuaranteesRootSectionFormValues
): TRootApplicationSectionDto => {
  const operationType = getSelectedGuaranteeOperationType(values.guaranteeOperationType);

  if (!operationType) {
    return {};
  }

  const isProzorroTender = isGuaranteeOperationTypeProzorro(operationType);
  const isOther = operationType === GuaranteeOperationType.OTHER;
  const payload: TRootApplicationSectionDto = {
    guaranteeOperationType: operationType,
    isUrgent: values.isUrgent,
  };

  if (isOther) {
    payload.guaranteeOperationTypeOther = values.guaranteeOperationTypeOther.trim();
  }

  if (!isProzorroTender) {
    payload.guaranteeAmount = Number(values.guaranteeAmount);
    payload.guaranteeCurrency = values.guaranteeCurrency?.value ?? undefined;
    payload.guaranteeExpiryDate = toInstantPayloadValue(values.guaranteeExpiryDate);
  }

  return payload;
};

export const buildGuaranteesSecretSectionPayload = (
  values: TGuaranteesRootSectionFormValues
): TGuaranteesSecuritySectionDto => {
  const securityType = getSelectedGuaranteeSecurityType(values.securityType);
  const payload: TGuaranteesSecuritySectionDto = {};

  if (!securityType) {
    return payload;
  }

  payload.type = securityType;
  payload.amount = Number(values.securityAmount);
  payload.requiredSecurityAmount = values.requiredSecurityAmount.trim();
  payload.pledgorName = values.pledgorName.trim();
  payload.pledgorHasChangesInConstituentDocuments =
    values.pledgorHasChangesInConstituentDocuments?.value ===
    GuaranteesPrincipalConstituentDocumentsChangesOption.YES;
  payload.pledgorIdentifyCode = payload.pledgorHasChangesInConstituentDocuments
    ? values.pledgorIdentifyCode.trim()
    : '';
  payload.principalDebitAccountIban = normalizeGuaranteesIban(values.principalDebitAccountIban);

  if (securityType === GuaranteeSecurityType.DEPOSIT_RIGHTS) {
    payload.depositAccountIban = normalizeGuaranteesIban(values.depositAccountIban);
    payload.coverageAccountIban = '';
  }

  if (securityType === GuaranteeSecurityType.COVERAGE_ACCOUNT) {
    payload.coverageAccountIban = normalizeGuaranteesIban(values.coverageAccountIban);
    payload.depositAccountIban = '';
  }

  if (securityType === GuaranteeSecurityType.NO_SECURITY) {
    payload.depositAccountIban = '';
    payload.coverageAccountIban = '';
  }

  return payload;
};

export const buildGuaranteesSecurityCurrenciesPayload = (
  values: TGuaranteesRootSectionFormValues
) => {
  const securityType = getSelectedGuaranteeSecurityType(values.securityType);

  return {
    currencies:
      securityType === GuaranteeSecurityType.NO_SECURITY
        ? []
        : getSecurityCurrencyValues(values.securityCurrencies),
  };
};

export const GUARANTEES_ROOT_SECTION_DRAFT_FIELD_NAMES = [
  'securityType',
  'isCounterpartyAccount',
  'isCounterpartyPrincipalDebitAccount',
  'securityAmount',
  'securityCurrencies',
  'requiredSecurityAmount',
  'pledgorName',
  'pledgorHasChangesInConstituentDocuments',
  'pledgorIdentifyCode',
  'principalDebitAccount',
  'principalDebitAccountIban',
  'account',
  'coverageAccountIban',
  'depositAccountIban',
  'contract',
  'mainDealId',
  'mainDealNumber',
  'mainDealDate',
  'mainDealDateFrom',
  'mainDealDateTo',
  'mainDealCurrency',
  'mainDealAmount',
  'mainDealLimitBalance',
] as const satisfies readonly (keyof TGuaranteesRootSectionFormValues)[];

type TGuaranteesRootSectionFieldName = keyof TGuaranteesRootSectionFormValues;

type TGuaranteesFieldStateGetter = (
  name: TGuaranteesRootSectionFieldName
) => { isDirty?: boolean };

export const getComparableFormValue = (value: unknown): unknown =>
  value !== null && typeof value === 'object' && 'value' in value
    ? (value as { value?: unknown }).value
    : value;

const normalizeComparableValue = (value: unknown): unknown => {
  if (value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    return value.map(normalizeComparableValue);
  }

  if (value !== null && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((accumulator, key) => {
        accumulator[key] = normalizeComparableValue((value as Record<string, unknown>)[key]);

        return accumulator;
      }, {});
  }

  return value;
};

export const getDirtyGuaranteesRootSectionDraftFieldNames = (
  getFieldState: TGuaranteesFieldStateGetter
): TGuaranteesRootSectionFieldName[] =>
  GUARANTEES_ROOT_SECTION_DRAFT_FIELD_NAMES.filter(
    (fieldName) => getFieldState(fieldName).isDirty
  );

export const buildGuaranteesRootSectionHydrationValues = ({
  currentValues,
  defaultValues,
  dirtyFieldNames = [],
}: {
  currentValues: TGuaranteesRootSectionFormValues;
  defaultValues: TGuaranteesRootSectionFormValues;
  dirtyFieldNames?: readonly TGuaranteesRootSectionFieldName[];
}): TGuaranteesRootSectionFormValues =>
  dirtyFieldNames.reduce<TGuaranteesRootSectionFormValues>(
    (hydrationValues, fieldName) => ({
      ...hydrationValues,
      [fieldName]: currentValues[fieldName],
    }),
    { ...defaultValues }
  );

export const areGuaranteesRootSectionFormValuesEqual = (
  firstValues: TGuaranteesRootSectionFormValues,
  secondValues: TGuaranteesRootSectionFormValues
): boolean =>
  JSON.stringify(normalizeComparableValue(firstValues)) ===
  JSON.stringify(normalizeComparableValue(secondValues));

export const shouldShowGuaranteesDependentSections = ({
  hasSavedSecurityAccount,
  isSecretSavedForCurrentApplication,
}: {
  hasSavedSecurityAccount: boolean;
  isSecretSavedForCurrentApplication: boolean;
}): boolean => hasSavedSecurityAccount || isSecretSavedForCurrentApplication;

export const shouldLoadGuaranteesDependentSections = ({
  applicationLoadKey,
  loadedDependentSectionsKey,
  shouldShowDependentSections,
}: {
  applicationLoadKey: string;
  loadedDependentSectionsKey: string;
  shouldShowDependentSections: boolean;
}): boolean =>
  shouldShowDependentSections &&
  !!applicationLoadKey &&
  loadedDependentSectionsKey !== applicationLoadKey;

export const isGuaranteesCheckApplicationDisabled = ({
  canCheckApplication,
  hasDependentSectionChanges,
  hasGuaranteeTypeChanges,
  isReadOnly,
  isStep1Busy,
  isStep1Complete,
}: {
  canCheckApplication: boolean;
  hasDependentSectionChanges: boolean;
  hasGuaranteeTypeChanges: boolean;
  isReadOnly: boolean;
  isStep1Busy: boolean;
  isStep1Complete: boolean;
}): boolean =>
  !canCheckApplication ||
  !isStep1Complete ||
  isStep1Busy ||
  isReadOnly ||
  hasGuaranteeTypeChanges ||
  hasDependentSectionChanges;

export const isGuaranteesClientApplicationReadOnly = ({
  applicationStatus,
  isAdministratorFlow,
}: {
  applicationStatus?: string | null;
  isAdministratorFlow: boolean;
}): boolean =>
  !isAdministratorFlow &&
  !!applicationStatus &&
  applicationStatus !== GuaranteesApplicationStatus.DRAFT;
